/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Apr 11 2024
 **/

import { useTheme } from "@emotion/react";

import { CircularProgress, Extensions, Icon } from "@/frameworks/designStuff/components";
import { Maybe, SwitchCase } from "@/frameworks/libraries/components";

import * as S from "../emotion";
import { useUploadTus } from "@/frameworks/core/hooks";
import { useFileExtensions } from "@/frameworks/core/utils";

type Status = "success" | "pending" | "error";

type FileUploadItemProps = {
	percentage: number;
	fileName: string;
	fileMimeType: string;
	status: Status;
	tusID: string;
};

export default function FileUploadItem(currentFile: FileUploadItemProps) {
	const { getFileExtension, getDroppedFileMimeType } = useFileExtensions();
	const theme = useTheme();
	const tusActions = useUploadTus();

	const cancelUpload = () => {
		tusActions.cancelUpload(currentFile.tusID);
		tusActions.removeUploadedState(currentFile.tusID);
	};

	// dropped file에서는 hwp, hwpx에 대한 type이 없음
	const fileMimeType = getDroppedFileMimeType(currentFile.fileMimeType, currentFile.fileName);

	return (
		<S.StyledFileUploadItem verticalAlign="center" horizonAlign="distribute" gap={8}>
			<S.FileExtensionWrapper verticalAlign="center" horizonAlign="center">
				<Extensions type={getFileExtension(fileMimeType)} />
			</S.FileExtensionWrapper>
			<S.FileName typoSize="Body2" typoColor={currentFile.status === "error" ? "error_60_100" : "neutral_70_100"}>
				{currentFile.fileName}
			</S.FileName>
			<S.UploadStateWrapper
				className={currentFile.status}
				status={currentFile.status}
				verticalAlign="center"
				horizonAlign="center"
			>
				<SwitchCase
					condition={currentFile.status}
					cases={{
						pending: <CircularProgress value={currentFile.percentage} />,
						success: <Icon name="Check24" fill={theme.colors.neutral_00_100} />,
						error: <Icon name="Close24" fill={theme.colors.neutral_00_100} />
					}}
				/>
			</S.UploadStateWrapper>
			<Maybe test={currentFile.status === "pending"}>
				<S.UploadStateWrapper
					className="cancel"
					status="cancel"
					verticalAlign="center"
					horizonAlign="center"
					onClick={cancelUpload}
				>
					<Icon name="Close24" fill={theme.colors.neutral_00_100} />
				</S.UploadStateWrapper>
			</Maybe>
		</S.StyledFileUploadItem>
	);
}
